import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IMPORT_LIMITS, REGISTER_COLUMNS, type ImportResult } from '@mahallu/shared';
import { ApiError, REGISTER_KEYS, registerSheet } from '../api';
import { FileField, FormSection, Modal } from '../components/Form';
import { Icon } from '../components/Icon';
import { Badge } from '../components/Parts';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

function Problems({ r }: { r: ImportResult }) {
  const { t, i18n } = useTranslation();
  const label = (key: string | null) => {
    const col = REGISTER_COLUMNS.find((c) => c.key === key);
    return col ? (i18n.language === 'ml' ? col.ml : col.en) : '—';
  };
  return (
    <>
      <p className="form-error" role="alert">
        {t('import.problems', { n: r.problemCount })}
      </p>
      <div className="tbl-wrap">
        <div className="tbl-scroll import-problems">
          <table>
            <thead>
              <tr>
                <th>{t('import.row')}</th>
                <th>{t('import.column')}</th>
                <th>{t('import.problem')}</th>
              </tr>
            </thead>
            <tbody>
              {r.problems.map((p, i) => (
                <tr key={i}>
                  <td className="num">{p.row || '—'}</td>
                  <td>{label(p.column)}</td>
                  <td>{p.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {r.problemCount > r.problems.length ? <p className="note">{t('import.moreProblems', { n: r.problemCount - r.problems.length })}</p> : null}
    </>
  );
}

/** Member Management › Import: template, check, then apply all at once (pilot slice 6, ADR-0031). */
export function RegisterImport({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [checked, setChecked] = useState<ImportResult | null>(null);
  const template = useMutation({ mutationFn: registerSheet.template });
  const run = useMutation({
    mutationFn: (mode: 'check' | 'apply') => registerSheet.import(mode, file!),
    onSuccess: async (r) => {
      setChecked(r);
      if (r.applied) await Promise.all(REGISTER_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey })));
    },
  });
  const r = checked;
  const clean = Boolean(r && r.problemCount === 0 && !r.applied);
  return (
    <Modal
      title={t('import.title')}
      onClose={onClose}
      wide
      footer={
        <>
          {run.isError ? (
            <p className="form-error" role="alert">
              {errorText(run.error)}
            </p>
          ) : null}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {r?.applied ? t('users.done') : t('form.cancel')}
          </button>
          {!r?.applied ? (
            <button type="button" className="btn" disabled={!file || run.isPending} onClick={() => run.mutate('check')}>
              {t('import.check')}
            </button>
          ) : null}
          {clean ? (
            <button type="button" className="btn primary" disabled={run.isPending} onClick={() => run.mutate('apply')}>
              {t('import.apply', { houses: r!.houses, members: r!.members })}
            </button>
          ) : null}
        </>
      }
    >
      <ol className="steps">
        <li>
          {t('import.step1')}{' '}
          <button type="button" className="btn sm" disabled={template.isPending} onClick={() => template.mutate()}>
            <Icon name="down" />
            {t('import.template')}
          </button>
          {template.isError ? <span className="form-error"> {errorText(template.error)}</span> : null}
        </li>
        <li>{t('import.step2')}</li>
        <li>{t('import.step3')}</li>
      </ol>
      <FormSection title={t('import.section')}>
        <FileField
          label={t('import.file')}
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          full
          hint={t('import.fileHint', { mb: IMPORT_LIMITS.bytes / 1024 / 1024, rows: IMPORT_LIMITS.rows })}
          onChange={(f) => {
            setFile(f);
            setChecked(null);
            run.reset();
          }}
        />
      </FormSection>
      {run.isPending ? <p className="note">{t('import.working')}</p> : null}
      {r && r.problemCount ? <Problems r={r} /> : null}
      {r && !r.problemCount ? (
        <div className="callout" role="status">
          {r.applied ? t('import.done', { houses: r.houses, members: r.members }) : t('import.ready', { houses: r.houses, members: r.members })}
        </div>
      ) : null}
      {r && r.possibleDuplicates.length ? (
        <div className="callout small" style={{ marginTop: 10 }}>
          <Badge tone="warn">{t('import.maybeDuplicates', { n: r.possibleDuplicates.length })}</Badge>
          <ul className="history">
            {r.possibleDuplicates.map((d) => (
              <li key={d.row}>{t('import.duplicateLine', { row: d.row, key: d.houseKey, houseNo: d.existingHouseNo })}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </Modal>
  );
}
